import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/archive", label: "考古合集" },
  { to: "/faq", label: "常见问题" },
  { to: "/about", label: "关于我们" },
];

const issueDate = new Date().toLocaleDateString("zh-CN", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden bg-foreground-950 text-background-100 md:block">
        <div className="mx-auto flex max-w-shell items-center justify-between px-6 py-1.5 text-[11px] tracking-wide">
          <span className="font-label">娱乐往事档案室 · 旧料翻出 · 年代考证</span>
          <span className="font-label text-background-300">第 199 期 · {issueDate}</span>
        </div>
      </div>

      <div
        className={`border-b transition-colors duration-300 ${
          scrolled
            ? "border-foreground-950/10 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="mx-auto flex max-w-shell items-center justify-between gap-6 px-4 py-3 md:px-6 md:py-4">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-archive-2-line text-2xl"></i>
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-xl font-black tracking-tight text-foreground-950">
                考古坞
              </span>
              <span className="hidden text-[11px] tracking-[0.24em] text-foreground-500 sm:block">
                KAOGU DOCK
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3.5 py-2 font-label text-sm transition-colors ${
                    isActive
                      ? "bg-primary-100 text-primary-800"
                      : "text-foreground-700 hover:bg-background-200 hover:text-foreground-950"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div
              className="relative"
              onMouseEnter={() => setCatOpen(true)}
              onMouseLeave={() => setCatOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCatOpen((prev) => !prev)}
                aria-expanded={catOpen}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3.5 py-2 font-label text-sm text-foreground-700 transition-colors hover:bg-background-200 hover:text-foreground-950"
              >
                分类归档
                <i className="ri-arrow-down-s-line text-base"></i>
              </button>
              {catOpen && (
                <div className="absolute right-0 top-full w-64 pt-2">
                  <div className="rounded-lg border border-foreground-950/10 bg-background-50 p-2 shadow-sm">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/archive?cat=${cat.id}`}
                        className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-accent-100"
                      >
                        <i className={`${cat.icon} text-lg text-primary-600`}></i>
                        <span className="flex-1 font-label">{cat.name}</span>
                        <span className="font-label text-xs text-foreground-400">
                          {cat.count}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/archive"
              className="hidden whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-secondary-600 sm:inline-flex"
            >
              <i className="ri-search-line mr-1.5 text-base"></i>
              检索旧料
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="打开导航菜单"
              aria-expanded={menuOpen}
              className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-md border border-foreground-950/15 text-foreground-900 lg:hidden"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-3-line"} text-xl`}></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-foreground-950/10 bg-background-50 lg:hidden">
            <nav className="mx-auto max-w-shell px-4 py-4" aria-label="移动端导航">
              <div className="flex flex-col">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `rounded-md px-3 py-3 font-label text-sm transition-colors ${
                        isActive
                          ? "bg-primary-100 text-primary-800"
                          : "text-foreground-700 hover:bg-background-200"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
              <p className="mt-4 mb-2 px-3 font-label text-xs tracking-[0.2em] text-foreground-400">
                分类归档
              </p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/archive?cat=${cat.id}`}
                    className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-800"
                  >
                    <i className={`${cat.icon} text-base text-primary-600`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;